import React, { useState, useRef, useEffect } from 'react';
import { GoogleMap, Marker, Autocomplete } from '@react-google-maps/api';
import { MapPin, Map, AlertCircle } from 'lucide-react';
import { cn } from '../shadcn/utils';
import { Dialog } from './Dialog';
import { Button } from '../shadcn/Button';
import Input from './Input';
import Notice from './Notice';
import { useGoogleMaps, useGoogleMapsEnabled } from '../context/GoogleMapsProvider';
import { COUNTRIES } from '../utils/constants';

interface GoogleAddressInputProps {
	initialAddress?: string;
	initialCoordinates?: { lat: number; lng: number } | null;
	country?: string;
	onChange?: (value: string, coordinates?: { lat: number; lng: number } | null) => void;
	className?: string;
	disabled?: boolean;
	readonly?: boolean;
}

// Country configuration for extensibility
const COUNTRY_CONFIG: Record<typeof COUNTRIES[number], { code: string; center: { lat: number; lng: number }; name: string }> = {
	venezuela: { code: 'VE', center: { lat: 10.4806, lng: -66.9036 }, name: 'Venezuela' },
	rd: { code: 'DO', center: { lat: 18.4861, lng: -69.9312 }, name: 'República Dominicana' },
	peru: { code: 'PE', center: { lat: -12.0464, lng: -77.0428 }, name: 'Perú' },
	colombia: { code: 'CO', center: { lat: 4.7110, lng: -74.0055 }, name: 'Colombia' },
	bolivia: { code: 'BO', center: { lat: -16.2902, lng: -63.5887 }, name: 'Bolivia' }
};

// Helper function to extract coordinates from Google Maps results
export const extractCoordinates = (place: google.maps.places.PlaceResult | null) => {
	if (!place?.geometry?.location) return null;

	return {
		lat: place.geometry.location.lat(),
		lng: place.geometry.location.lng()
	};
};

export default function GoogleAddressInput({
	initialAddress = '',
	initialCoordinates = null,
	onChange,
	className,
	disabled = false,
	readonly = false,
	country = 'venezuela'
}: GoogleAddressInputProps) {
	const { isLoaded, loadError, isApiKeyMissing } = useGoogleMaps();
	const isGoogleMapsEnabled = useGoogleMapsEnabled();

	const [address, setAddress] = useState(() => initialAddress);
	const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(() => initialCoordinates);

	const [confirmed, setConfirmed] = useState(() => !!initialCoordinates);
	const [showMapModal, setShowMapModal] = useState(false);
	const [mapSearchValue, setMapSearchValue] = useState('');
	const [showAddressSuggestion, setShowAddressSuggestion] = useState(false);
	const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);

	const geocoder = useRef<google.maps.Geocoder | null>(null);

	// Get country configuration with fallback to Venezuela
	const getCountryConfig = () => {
		return COUNTRY_CONFIG[country as keyof typeof COUNTRY_CONFIG] || COUNTRY_CONFIG['venezuela'];
	};

	// Default center coordinates by country
	const getDefaultCenter = () => {
		return getCountryConfig().center;
	};

	// Initialize geocoder when Google Maps is loaded
	useEffect(() => {
		if (isLoaded && window.google?.maps?.Geocoder) {
			geocoder.current = new window.google.maps.Geocoder();
		}
	}, [isLoaded]);

	const onMapLoad = (map: google.maps.Map) => {
		// Geocoder should already be initialized in useEffect
		if (isLoaded && window.google?.maps?.Geocoder && !geocoder.current) {
			geocoder.current = new window.google.maps.Geocoder();
		}
	};

	const injectGooglePlacesStyles = () => {
		const styleId = 'google-places-modal-styles';
		if (!document.getElementById(styleId)) {
			const style = document.createElement('style');
			style.id = styleId;
			style.textContent = `
        .pac-container {
          z-index: 10000 !important;
          position: fixed !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.1) !important;
          border: 1px solid #374151 !important;
          background-color: #0a0a0a !important;
          margin-top: 4px !important;
          font-family: inherit !important;
          overflow: visible !important;
          pointer-events: auto !important;
        }
        
        .pac-item {
          background-color: #0a0a0a !important;
          color: #e5e7eb !important;
          border-top: 1px solid #374151 !important;
          padding: 10px 12px !important;
          cursor: pointer !important;
          font-size: 14px !important;
          line-height: 1.4 !important;
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          position: relative !important;
          pointer-events: auto !important;
        }
        
        .pac-item:first-child {
          border-top: none !important;
        }
        
        .pac-item:hover,
        .pac-item:focus {
          background-color: #1f2937 !important;
          outline: none !important;
        }
        
        .pac-item-selected {
          background-color: #1f2937 !important;
        }
        
        .pac-item-query {
          color: #f3f4f6 !important;
        }
        
        .pac-matched {
          color: #60a5fa !important;
          font-weight: 500 !important;
        }
        
        .pac-icon {
          display: none !important;
        }
        
        .pac-icon-marker {
          display: none !important;
        }
        
        .pac-logo:after {
          display: none !important;
        }
        
        .pac-container:after {
          content: "" !important;
          display: none !important;
        }
      `;
			document.head.appendChild(style);
		}
	};

	const onSearchAutocompleteLoad = (autoC: google.maps.places.Autocomplete) => {
		setAutocomplete(autoC);

		// Set country restrictions based on the country prop
		const countryCode = getCountryConfig().code;

		autoC.setComponentRestrictions({ country: [countryCode] });

		// Inject styles to ensure dropdown appears above map
		injectGooglePlacesStyles();
	};

	const onSearchPlaceChanged = () => {
		if (autocomplete !== null) {
			const place = autocomplete.getPlace();

			if (place && place.geometry?.location) {
				const coords = extractCoordinates(place);
				if (coords) {
					setCoordinates(coords);
				}

				// Update the search input with the selected place name
				if (place.formatted_address || place.name) {
					setMapSearchValue(place.name! || place.formatted_address!);
				}
			}
		}
	};

	const onMarkerDragEnd = (event: google.maps.MapMouseEvent) => {
		if (!event.latLng) return;

		const newLat = event.latLng.lat();
		const newLng = event.latLng.lng();

		const newCoords = { lat: newLat, lng: newLng };
		setCoordinates(newCoords);
	};

	const handleMapSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setMapSearchValue(e.target.value);
	};

	const openMapModal = () => {
		setShowMapModal(true);
		setMapSearchValue('');
		setShowAddressSuggestion(false); // Hide any existing suggestion when opening modal
	};

	const closeMapModal = () => {
		setShowMapModal(false);
	};

	const confirmLocation = () => {
		setConfirmed(true);
		setShowMapModal(false);

		// Show address suggestion if mapSearchValue is valid and different from placeholder
		if (mapSearchValue && mapSearchValue.trim() && mapSearchValue !== 'Dirección y referencias del local') {
			setShowAddressSuggestion(true);
		}

		onChange?.(address, coordinates);
	};

	const acceptAddressSuggestion = () => {
		setAddress(mapSearchValue);
		setShowAddressSuggestion(false);
		onChange?.(mapSearchValue, coordinates);
	};

	const handleMapClick = (event: google.maps.MapMouseEvent) => {
		if (!event.latLng) return;

		const newLat = event.latLng.lat();
		const newLng = event.latLng.lng();

		const newCoords = { lat: newLat, lng: newLng };
		setCoordinates(newCoords);
	};

	// Loading component while Google Maps loads
	const GoogleMapsLoading = () => (
		<div className={cn('space-y-4', className)}>
			<div className="rounded-lg border border-neutral-700 bg-neutral-800/30 p-4">
				<div className="flex gap-3">
					<div className="animate-spin">
						<Map className="size-5 text-neutral-400" />
					</div>
					<div className="text-sm text-neutral-300">
						<p className="font-medium">Cargando Google Maps...</p>
						<p className="text-neutral-400">Por favor espere mientras se cargan los mapas.</p>
					</div>
				</div>
			</div>
		</div>
	);

	// Error component if Google Maps fails to load
	const GoogleMapsError = () => (
		<div className={cn('space-y-4', className)}>
			<div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
				<div className="flex gap-3">
					<AlertCircle className="mt-0.5 size-5 shrink-0 text-red-400" />
					<div className="text-sm text-red-200">
						<p className="mb-2 font-semibold">{isApiKeyMissing ? 'Configuración Pendiente' : 'Error del Sistema'}</p>
						<p className="mb-3">
							{isApiKeyMissing
								? 'La funcionalidad de mapas no está configurada actualmente.'
								: 'No se pudo cargar Google Maps, que es requerido para seleccionar la ubicación exacta de su local.'}
						</p>
						<p className="font-medium">
							{isApiKeyMissing
								? 'Puede continuar usando la aplicación. Las funciones de mapa estarán disponibles próximamente.'
								: 'Por favor contacte a soporte técnico para resolver este problema. Puede continuar usando el resto de la aplicación.'}
						</p>
					</div>
				</div>
			</div>

			{/* Still show the reference input even when maps fail */}
			<Input
				placeholder="Dirección y referencias del local"
				value={address}
				onChange={(value) => {
					setAddress(value?.trim() || '');
					onChange?.(value?.trim() || '', coordinates);
				}}
				disabled={disabled}
				readonly={readonly}
				floatingLabel="Dirección y referencias"
				Icon={<MapPin className="size-4" />}
				className={cn({
					'!border-red-400 !ring-2 !ring-red-400': !address?.trim()
				})}
				containerClassName="!mt-3"
			/>
		</div>
	);

	// Show loading state while Google Maps is loading
	if (!isLoaded && !loadError && !isApiKeyMissing) {
		return <GoogleMapsLoading />;
	}

	// Show error state if Google Maps failed to load, but still allow text input
	if (!isGoogleMapsEnabled) {
		return <GoogleMapsError />;
	}

	// Render the component only when Google Maps is loaded
	return (
		<div className={cn('space-y-1', className)}>
			{!readonly && !disabled && (
				<button
					type="button"
					onClick={openMapModal}
					className={cn('!-mt-1 flex w-full items-center gap-2 rounded-lg border-2 border-dashed p-3 text-sm transition-colors', {
						'border-green-500 bg-green-500/10 text-green-400': coordinates && confirmed,
						'border-red-400 bg-red-500/10 text-red-400 hover:border-red-300': !coordinates || !confirmed,
						'border-neutral-600 bg-neutral-800/30 text-neutral-400 hover:border-neutral-500': coordinates && confirmed
					})}
				>
					<Map className="size-4 shrink-0" />
					<div className="flex-1 text-left">
						{coordinates && confirmed ? (
							<div>
								<p className="font-medium">Ubicación seleccionada</p>
								<p className="text-xs opacity-75">Haga click para cambiar la ubicación</p>
							</div>
						) : (
							<div>
								<p className="font-medium">Seleccionar ubicación en el mapa (Requerido)</p>
								<p className="text-xs opacity-75">Haga clic para abrir el mapa y seleccionar las coordenadas exactas</p>
							</div>
						)}
					</div>
					{(!coordinates || !confirmed) && <AlertCircle className="size-4 shrink-0" />}
				</button>
			)}

			<Input
				placeholder="Dirección y referencias del local"
				value={address}
				onChange={(value) => {
					setAddress(value?.trim() || '');
					setShowAddressSuggestion(false); // Hide suggestion when user types
					onChange?.(value?.trim() || '', coordinates);
				}}
				disabled={disabled}
				readonly={readonly}
				floatingLabel="Dirección y referencias"
				Icon={<MapPin className="size-4" />}
				className={cn({
					'!border-red-400 !ring-2 !ring-red-400': !address?.trim()
				})}
				containerClassName="!mt-3"
			/>

			{/* Address suggestion hint */}
			{showAddressSuggestion && mapSearchValue && (
				<div className="mt-2 flex items-center gap-2 text-xs text-blue-400">
					<span className="opacity-75">¿Usar esta dirección?</span>
					<button
						type="button"
						onClick={acceptAddressSuggestion}
						className="rounded-md bg-blue-500/10 px-2 py-1 text-blue-400 hover:bg-blue-500/20 transition-colors underline"
					>
						{mapSearchValue}
					</button>
				</div>
			)}

			{/* Map Modal */}
			{showMapModal && (
				<Dialog title="" onClose={closeMapModal} className="!w-[90vw] !max-w-4xl !px-0" cerrarButton={false} closable={false}>
					<div className="space-y-4 px-8">
						<Notice
							message="Si no selecciona una ubicación precisa, nuestros usuarios no podrán encontrar su local. Use la búsqueda para encontrar una ubicación aproximada, luego haga clic en el mapa o arrastre el marcador para seleccionar la ubicación exacta."
							variant="warning"
							className="mb-3"
						/>

						{/* Search within map */}
						<Autocomplete className="z-[1000]" onLoad={onSearchAutocompleteLoad} onPlaceChanged={onSearchPlaceChanged}>
							<div className="relative">
								<input
									type="text"
									value={mapSearchValue}
									onChange={handleMapSearchChange}
									placeholder="Buscar lugar para ubicación aproximada..."
									className="h-10 w-full rounded-lg border border-neutral-700 bg-neutral-925 px-3 pr-10 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
								/>
								<MapPin className="absolute right-3 top-3 size-4 text-neutral-400" />
							</div>
						</Autocomplete>

						<div className="z-[999] overflow-hidden rounded-lg border border-neutral-800">
							<div className="h-96 w-full">
								<GoogleMap
									mapContainerStyle={{ width: '100%', height: '100%' }}
									center={coordinates || getDefaultCenter()}
									zoom={coordinates ? 16 : 12}
									onLoad={onMapLoad}
									onClick={handleMapClick}
									options={{
										styles: [
											{
												featureType: 'all',
												elementType: 'geometry.fill',
												stylers: [{ color: '#1f2937' }]
											},
											{
												featureType: 'all',
												elementType: 'labels.text.fill',
												stylers: [{ color: '#e5e7eb' }]
											},
											{
												featureType: 'all',
												elementType: 'labels.text.stroke',
												stylers: [{ color: '#1f2937' }]
											},
											{
												featureType: 'road',
												elementType: 'geometry.fill',
												stylers: [{ color: '#374151' }]
											},
											{
												featureType: 'water',
												elementType: 'geometry.fill',
												stylers: [{ color: '#0f172a' }]
											}
										],
										disableDefaultUI: false,
										zoomControl: true,
										mapTypeControl: true,
										streetViewControl: true,
										fullscreenControl: true
									}}
								>
									{coordinates && (
										<Marker
											position={coordinates}
											draggable={true}
											onDragEnd={onMarkerDragEnd}
											title="Arrastra para cambiar la ubicación exacta"
										/>
									)}
								</GoogleMap>
							</div>
						</div>

						<div className="flex justify-between gap-2 pt-2">
							<Button onClick={closeMapModal} variant="secondary">
								Cancelar
							</Button>
							<Button onClick={confirmLocation} disabled={!coordinates} variant="primary">
								{coordinates ? 'Confirmar Ubicación' : 'Seleccione una ubicación'}
							</Button>
						</div>
					</div>
				</Dialog>
			)}
		</div>
	);
}
